import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { HexagonMesh } from "@/components/HexagonMesh";
import { ParticleField } from "@/components/ParticleField";
import { TechLines } from "@/components/TechLines";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { TrustBadges } from "@/components/TrustBadges";
import { Clock, Target, TrendingUp, Layers, Eye, ArrowRight, Zap, Shield, Sparkles } from "lucide-react";
import logo from "@/assets/fh-logo.png";
const Index = () => {
  const [animatedCards, setAnimatedCards] = useState<Set<number>>(new Set());
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setAnimatedCards(prev => new Set(prev).add(index));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);
  
  const scrollToContact = () => {
    window.location.href = '/contact';
  };
  const benefits = [{
    icon: Clock,
    title: "Save Time",
    description: "Automate repetitive tasks and streamline workflows to focus on what matters most."
  }, {
    icon: Target,
    title: "Reduce Errors",
    description: "Eliminate manual mistakes with intelligent validation and automated processes."
  }, {
    icon: TrendingUp,
    title: "Increase Revenue",
    description: "Optimize operations and unlock exponential growth with data-driven insights."
  }, {
    icon: Shield,
    title: "Organize Chaos",
    description: "Transform scattered information into unified, intelligent systems."
  }, {
    icon: Eye,
    title: "Real-Time Control",
    description: "Gain instant visibility and complete control over your business operations."
  }, {
    icon: Layers,
    title: "Fully Integrated",
    description: "Connect all your tools and platforms into one seamless, efficient ecosystem."
  }];
  return <div className="min-h-screen bg-deep-navy overflow-x-hidden">
      <Navigation />

      {/* HERO SECTION */}
      <section className="relative flex items-center justify-center md:min-h-screen pt-16 md:pt-20 pb-12 md:pb-32 px-2 md:px-4">
        {/* Background Layers */}
        <div className="absolute inset-0 bg-gradient-to-b from-deep-navy via-black to-deep-navy" />
        <HexagonMesh />
        <ParticleField />
        
        {/* Central Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-radiant-blue/20 rounded-full blur-[200px] animate-glow-pulse" />

        <div className="container mx-auto relative z-10">
          <div className="max-w-6xl mx-auto text-center space-y-4 md:space-y-10">
            {/* Glass Panel Container */}
            <div className="glass-panel rounded-[2rem] p-5 md:p-10 lg:p-16 backdrop-blur-3xl shadow-[0_8px_64px_rgba(0,20,42,0.8),inset_0_1px_2px_rgba(45,184,255,0.2)] animate-scale-in my-[20px]">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full glass-panel-light mb-4 md:mb-10 animate-fade-in">
                <Sparkles className="w-4 h-4 text-neon-glow" />
                <span className="text-sm font-semibold text-neon-glow uppercase tracking-wider">
                  Next-Generation Business Systems
                </span>
              </div>
              
              {/* Headline */}
              <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-display font-bold leading-[1.1] text-white mb-4 md:mb-8 animate-fade-in-up">
                Run Your Business with{" "}
                <span className="relative inline-block mt-2">
                  <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-neon-glow via-radiant-blue to-neon-glow neon-text">
                    Clarity and Control
                  </span>
                </span>
              </h1>
              
              {/* Subtitle */}
              <p className="text-base sm:text-lg md:text-xl text-gray-200 max-w-4xl mx-auto font-light leading-relaxed mb-6 md:mb-12 animate-fade-in-up" style={{
              animationDelay: "0.2s"
            }}>
                We build custom business systems, websites, automations, and AI-powered solutions - tailored to your unique needs.
From CRM apps to internal platforms, we create smart, efficient tools that save you time, money, reduce friction, and help your business grow - faster and more affordably than traditional development methods.                                                                                                                                      
              </p>
              
              {/* CTA Button */}
              <div className="flex justify-center animate-fade-in-up" style={{
              animationDelay: "0.4s"
            }}>
                <Button onClick={scrollToContact} size="lg" className="group relative bg-gradient-to-r from-neon-glow to-radiant-blue hover:from-radiant-blue hover:to-neon-glow text-deep-navy font-bold text-sm sm:text-base px-6 sm:px-8 h-12 sm:h-14 rounded-full overflow-hidden transition-all duration-500 hover:scale-110 shadow-[0_0_40px_rgba(45,184,255,0.6)] hover:shadow-[0_0_60px_rgba(45,184,255,0.8)]">
                  <span className="relative z-10 flex items-center gap-2">
                    START YOUR PROJECT NOW
                    <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-2 transition-transform duration-300" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Button>
          </div>
        </div>
      </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-neon-glow/40 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-neon-glow rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="relative py-12 md:py-32 px-2 md:px-4 bg-gradient-to-b from-black via-deep-navy to-black">
        <TechLines />
        
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-8 md:mb-20 animate-fade-in">
            <div className="inline-block glass-panel-light px-6 py-3 rounded-full mb-6 md:mb-8">
              <span className="text-sm font-bold text-neon-glow uppercase tracking-widest">Why Choose Us</span>
            </div>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-6 md:mb-8 leading-tight">
              Built for the Future<br />of Business
            </h2>
            <p className="text-base md:text-xl text-gray-400 max-w-3xl mx-auto font-light">
              Cutting-edge digital solutions powered by AI and modern technology
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-5 max-w-7xl mx-auto">
            {benefits.map((benefit, index) => {
            const hasAnimated = animatedCards.has(index);
            return <div 
              key={index} 
              ref={(el) => cardsRef.current[index] = el}
              data-index={index}
              className={`group relative gradient-border hover-glow ${hasAnimated ? 'animate-fade-in' : 'opacity-0'}`} 
              style={hasAnimated ? {
                animationDelay: `${index * 0.15}s`
              } : undefined}
            >
                  <div className="relative p-2.5 md:p-5 lg:p-7 rounded-lg md:rounded-xl bg-gradient-to-br from-dark-blue/60 via-deep-navy/40 to-black/60 backdrop-blur-xl h-full w-full">
                    {/* Icon and Title */}
                    <div className="flex items-center gap-2 md:flex-col md:items-center md:gap-0 mb-2 md:mb-3">
                      <div className="relative">
                        <div className="h-8 w-8 md:h-12 md:w-12 rounded-lg bg-gradient-to-br from-neon-glow/20 to-radiant-blue/20 border-2 border-neon-glow/40 flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-[0_0_20px_rgba(45,184,255,0.3)]">
                          <benefit.icon className="h-4 w-4 md:h-6 md:w-6 text-neon-glow" strokeWidth={1.5} />
                        </div>
                        <div className="absolute inset-0 bg-neon-glow/20 rounded-lg blur-lg group-hover:blur-xl transition-all duration-500" />
                      </div>
                      
                      <h3 className="text-sm md:text-lg lg:text-xl font-display font-bold text-white group-hover:text-neon-glow transition-colors duration-300 leading-tight md:mt-2">
                        {benefit.title}
                      </h3>
                    </div>
                    
                    {/* Description - always visible */}
                    <div className="mt-2">
                      <p className="text-gray-400 leading-snug text-[11px] md:text-sm">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* WHAT WE DO SECTION */}
      <section className="relative py-12 md:py-32 px-2 md:px-4 bg-deep-navy">
        <div className="absolute inset-0 hexagon-bg opacity-40" />

        <div className="container mx-auto relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="glass-panel rounded-[2rem] p-4 md:p-8 lg:p-12 xl:p-20 text-center hover-glow">
              <div className="inline-block glass-panel-light px-6 py-3 rounded-full mb-8">
                <span className="text-sm font-bold text-neon-glow uppercase tracking-widest">Services</span>
              </div>
              
              <h2 className="text-5xl md:text-6xl font-display font-bold text-white mb-8 leading-tight">
                What We Build
              </h2>
              <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto">
                From lean automations to full-scale management platforms, we create digital infrastructures that simplify operations, boost efficiency, and evolve with your business.
Every tool is crafted with purpose: faster to build, smarter to run, and built to grow with you.
              </p>
              
              <Link to="/services">
                <Button size="lg" className="group glass-panel-light hover:glass-panel border-2 border-neon-glow/40 hover:border-neon-glow text-white font-semibold text-lg px-10 h-16 rounded-full transition-all duration-500 hover:scale-110 hover:shadow-[0_0_50px_rgba(45,184,255,0.5)]">
                  Explore Our Services
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform duration-300" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section className="relative py-16 md:py-32 px-2 md:px-4 bg-gradient-to-b from-black via-dark-blue/30 to-black">
        
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-20">
            <div className="inline-block glass-panel-light px-6 py-3 rounded-full mb-8">
              <span className="text-sm font-bold text-neon-glow uppercase tracking-widest">Our Process</span>
            </div>
            <h2 className="text-5xl md:text-7xl font-display font-bold text-white mb-8 leading-tight max-w-5xl mx-auto">
              The Clarity-to-Control<br />Method
            </h2>
            <p className="text-xl md:text-2xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              Our proven 5-step process transforms business chaos into streamlined systems
              with complete visibility and control
            </p>
          </div>

          <div className="max-w-2xl mx-auto mb-16">
            <Link to="/how-it-works">
              <Button size="lg" className="w-full group relative bg-gradient-to-r from-neon-glow to-radiant-blue hover:from-radiant-blue hover:to-neon-glow text-deep-navy font-bold text-lg px-10 h-16 rounded-full transition-all duration-500 hover:scale-105 shadow-[0_0_50px_rgba(45,184,255,0.6)] hover:shadow-[0_0_70px_rgba(45,184,255,0.8)]">
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Learn How It Works
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-2 transition-transform duration-300" />
                </span>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="relative py-12 md:py-32 px-2 md:px-4 bg-gradient-to-br from-deep-navy via-black to-deep-navy overflow-hidden">
        {/* Dramatic Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full max-w-4xl">
          <div className="w-full h-full bg-radiant-blue/30 rounded-full md:blur-[250px] blur-[80px] animate-glow-pulse" />
        </div>

        <div className="container mx-auto relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-8xl font-display font-bold text-white mb-6 md:mb-10 leading-[1.1]">
              Ready to{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-neon-glow via-radiant-blue to-neon-glow neon-text">
                  Transform
                </span>
              </span>
              <br />Your Business?
            </h2>
            
            <p className="text-base sm:text-lg md:text-2xl text-gray-300 mb-8 md:mb-16 font-light leading-relaxed max-w-3xl mx-auto">
              Get in touch and let's discuss how we can help you<br className="hidden md:block" />
              run smarter and scale faster
            </p>
            
            <Button onClick={scrollToContact} size="lg" className="group relative bg-gradient-to-r from-neon-glow via-radiant-blue to-neon-glow hover:from-radiant-blue hover:via-neon-glow hover:to-radiant-blue text-deep-navy font-bold text-sm sm:text-base md:text-lg px-8 md:px-12 h-14 md:h-16 rounded-full transition-all duration-500 hover:scale-110 shadow-[0_0_60px_rgba(45,184,255,0.8)] hover:shadow-[0_0_100px_rgba(45,184,255,1)]">
              <span className="relative z-10 flex items-center gap-2">
                <Zap className="h-4 w-4 md:h-5 md:w-5" />
                Get Started Today
                <ArrowRight className="h-4 w-4 md:h-5 md:w-5 group-hover:translate-x-3 transition-transform duration-300" />
              </span>
            </Button>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center gap-8 mt-20 pt-16 border-t border-neon-glow/20">
              <div className="flex items-center gap-3 text-gray-400 hover:text-neon-glow transition-colors">
                <div className="w-2 h-2 rounded-full bg-neon-glow animate-pulse shadow-[0_0_10px_hsl(199,100%,58%)]" />
                <span className="text-sm uppercase tracking-wide">No long-term contracts</span>
              </div>
              <div className="flex items-center gap-3 text-gray-400 hover:text-neon-glow transition-colors">
                <div className="w-2 h-2 rounded-full bg-neon-glow animate-pulse shadow-[0_0_10px_hsl(199,100%,58%)]" style={{
                animationDelay: "0.5s"
              }} />
                <span className="text-sm uppercase tracking-wide">Free consultation</span>
              </div>
              <div className="flex items-center gap-3 text-gray-400 hover:text-neon-glow transition-colors">
                <div className="w-2 h-2 rounded-full bg-neon-glow animate-pulse shadow-[0_0_10px_hsl(199,100%,58%)]" style={{
                animationDelay: "1s"
              }} />
                <span className="text-sm uppercase tracking-wide">Fast turnaround</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      

      <FloatingWhatsApp phoneNumber="447428591835" />
      
      {/* Alternative WhatsApp Link */}
      
      
      <Footer />
    </div>;
};
export default Index;